/**
 * Compliance DSR — data-layer client.
 *
 * Console for the tenant privacy operator to submit + triage Data Subject
 * Requests against the two regulatory frameworks Trackerity currently
 * implements:
 *   - GDPR  (EU)   → /api/v1/gdpr-dsr  (feature #33 API)
 *   - CCPA  (US-CA) → /api/v1/ccpa-dsr  (feature #34 API)
 *
 * The two backends share a similar shape but diverge on status vocabulary
 * and SLA clock:
 *   - GDPR:  status ∈ pending | in_progress | fulfilled | rejected
 *            sla_deadline_ms = requested_at + 30 days
 *   - CCPA:  status ∈ received | verified | fulfilled | denied
 *            sla clock starts at received_at, 45-day horizon
 *
 * This module normalises both into a single {DsrRow, DsrFramework, DsrSla}
 * shape so the console UI can render a single table and form. Wherever the
 * two frameworks disagree we preserve the raw row under `raw` for operator
 * audit. Kept dep-free beyond the shared `apiClient`.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Framework + vocabulary
// ---------------------------------------------------------------------------

export const DSR_FRAMEWORKS = ["gdpr", "ccpa"] as const;
export type DsrFramework = (typeof DSR_FRAMEWORKS)[number];

export const REGIONS = ["EU", "US-CA"] as const;
export type DsrRegion = (typeof REGIONS)[number];

/** Framework-aligned default region (UI hint only — operator may override). */
export const DEFAULT_REGION_FOR: Record<DsrFramework, DsrRegion> = {
  gdpr: "EU",
  ccpa: "US-CA",
};

/** Request kinds surfaced in the submit form, per task spec. */
export const DSR_KINDS = ["access", "erasure", "portability"] as const;
export type DsrKind = (typeof DSR_KINDS)[number];

export const KIND_LABELS: Record<DsrKind, string> = {
  access: "Access",
  erasure: "Erasure",
  portability: "Portability",
};

/**
 * CCPA does not use the same vocabulary. Map the form's unified kind to the
 * closest CCPA request type so a single form can submit to either backend:
 *   - access       → know            (right to know)
 *   - erasure      → delete          (right to delete)
 *   - portability  → know            (portability is a sub-right of know in
 *                                     CCPA, satisfied by the same payload)
 */
export function mapKindToCcpaType(kind: DsrKind): "know" | "delete" {
  return kind === "erasure" ? "delete" : "know";
}

// ---------------------------------------------------------------------------
// Unified row shape — the console renders this, regardless of framework.
// ---------------------------------------------------------------------------

export type UnifiedStatus =
  | "pending"
  | "in_progress"
  | "fulfilled"
  | "rejected"
  | "received"
  | "verified"
  | "denied";

export interface DsrRow {
  framework: DsrFramework;
  id: string;
  email: string;
  kind: DsrKind | "other";
  status: UnifiedStatus;
  submitted_at: number;
  due_at: number;
  sla_breached: boolean;
  region: DsrRegion;
  raw: unknown;
}

// ---------------------------------------------------------------------------
// SLA constants — GDPR gives 30 days, CCPA gives 45.
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;
export const GDPR_SLA_MS = 30 * DAY_MS;
export const CCPA_SLA_MS = 45 * DAY_MS;

export type SlaTier = "green" | "amber" | "red";

/**
 * Render a due_at timestamp into a traffic-light tier:
 *   - red    if due_at has passed (breached) or ≤1 day remaining
 *   - amber  if ≤7 days remaining
 *   - green  otherwise
 * Exposed as a pure function so the vitest suite can assert the thresholds
 * without rendering the whole tree.
 */
export function slaTier(dueAtMs: number, nowMs: number): SlaTier {
  const remaining = dueAtMs - nowMs;
  if (remaining <= DAY_MS) return "red";
  if (remaining <= 7 * DAY_MS) return "amber";
  return "green";
}

/** Human-friendly countdown string, e.g. "12d 4h" / "3h 12m" / "breached". */
export function formatSlaCountdown(dueAtMs: number, nowMs: number): string {
  const remaining = dueAtMs - nowMs;
  if (remaining <= 0) return "breached";
  const days = Math.floor(remaining / DAY_MS);
  const hours = Math.floor((remaining % DAY_MS) / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// ---------------------------------------------------------------------------
// Input validation — hand-rolled safeParse to stay dep-free on zod.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
  success: true;
  data: T;
}
export interface ParseErr {
  success: false;
  errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface SubmitInput {
  email: string;
  kind: DsrKind;
  region: DsrRegion;
  framework: DsrFramework;
}

const EMAIL_MAX = 254;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const submitDsrSchema = {
  safeParse(input: Partial<SubmitInput>): ParseResult<SubmitInput> {
    const errors: Record<string, string> = {};
    const email = (input.email ?? "").trim();
    if (email.length === 0) {
      errors.email = "Subject email is required.";
    } else if (email.length > EMAIL_MAX) {
      errors.email = `Email must be ${EMAIL_MAX} chars or fewer.`;
    } else if (!EMAIL_RE.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    if (!input.kind || !DSR_KINDS.includes(input.kind)) {
      errors.kind = "Pick a request kind.";
    }
    if (!input.region || !REGIONS.includes(input.region)) {
      errors.region = "Pick a region.";
    }
    if (!input.framework || !DSR_FRAMEWORKS.includes(input.framework)) {
      errors.framework = "Framework is required.";
    }

    if (Object.keys(errors).length > 0) return { success: false, errors };
    return {
      success: true,
      data: {
        email,
        kind: input.kind as DsrKind,
        region: input.region as DsrRegion,
        framework: input.framework as DsrFramework,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Normalisers — raw API row → unified DsrRow.
// ---------------------------------------------------------------------------

interface GdprApiRow {
  id: string;
  tenant_id: string;
  subject_email: string;
  type: string;
  status: string;
  requested_at: number;
  sla_deadline_ms?: number;
  sla_breached?: boolean;
  region?: string;
}

interface CcpaApiRow {
  id: string;
  tenant_id: string;
  subject_email: string;
  type: string;
  status: string;
  received_at: number;
  days_since_received?: number;
  sla_breached?: boolean;
}

function normaliseKind(raw: string): DsrKind | "other" {
  if (raw === "access" || raw === "erasure" || raw === "portability") return raw;
  if (raw === "know") return "access";
  if (raw === "delete") return "erasure";
  return "other";
}

export function normaliseGdpr(row: GdprApiRow): DsrRow {
  const dueAt = row.sla_deadline_ms ?? row.requested_at + GDPR_SLA_MS;
  const region: DsrRegion = row.region === "US-CA" ? "US-CA" : "EU";
  return {
    framework: "gdpr",
    id: row.id,
    email: row.subject_email,
    kind: normaliseKind(row.type),
    status: row.status as UnifiedStatus,
    submitted_at: row.requested_at,
    due_at: dueAt,
    sla_breached: Boolean(row.sla_breached ?? dueAt < Date.now()),
    region,
    raw: row,
  };
}

export function normaliseCcpa(row: CcpaApiRow): DsrRow {
  const dueAt = row.received_at + CCPA_SLA_MS;
  return {
    framework: "ccpa",
    id: row.id,
    email: row.subject_email,
    kind: normaliseKind(row.type),
    status: row.status as UnifiedStatus,
    submitted_at: row.received_at,
    due_at: dueAt,
    sla_breached: Boolean(row.sla_breached ?? dueAt < Date.now()),
    region: "US-CA",
    raw: row,
  };
}

// ---------------------------------------------------------------------------
// Endpoint paths — root-owned wire-up mounts these at /api/v1/<path>.
// ---------------------------------------------------------------------------

const GDPR_BASE = "/gdpr-dsr";
const CCPA_BASE = "/ccpa-dsr";

/**
 * Returns the POST target for a given framework. Exposed so the vitest suite
 * can assert that switching tabs flips the submit target without having to
 * render the whole form.
 */
export function submitEndpointFor(framework: DsrFramework): string {
  return framework === "gdpr"
    ? `${GDPR_BASE}/requests`
    : `${CCPA_BASE}/requests`;
}

/**
 * Returns the GET target for the open-requests table. The underlying API
 * calls this `/requests`; callers commonly refer to "/open" in UX copy.
 * The query param `?status=` is applied by the caller when needed.
 */
export function openListEndpointFor(framework: DsrFramework): string {
  return framework === "gdpr"
    ? `${GDPR_BASE}/requests`
    : `${CCPA_BASE}/requests`;
}

/** Status-update endpoint per row. */
export function statusEndpointFor(framework: DsrFramework, id: string): string {
  return framework === "gdpr"
    ? `${GDPR_BASE}/requests/${encodeURIComponent(id)}/status`
    : `${CCPA_BASE}/requests/${encodeURIComponent(id)}/status`;
}

// ---------------------------------------------------------------------------
// Network operations
// ---------------------------------------------------------------------------

export interface SubmitDsrResult {
  row: DsrRow;
  /** Present for GDPR flow — the subject receives this via email to verify. */
  verification_token?: string;
}

export async function submitDsr(input: SubmitInput): Promise<SubmitDsrResult> {
  if (input.framework === "gdpr") {
    const res = await apiClient.post<{
      id: string;
      type: string;
      status: string;
      region?: string;
      sla?: { deadline_ms: number };
      verification?: { token?: string };
    }>(submitEndpointFor("gdpr"), {
      tenant_id: "demo", // tenant resolution is handled server-side via header
      email: input.email,
      type: input.kind,
    });
    const nowMs = Date.now();
    const dueAt = res.sla?.deadline_ms ?? nowMs + GDPR_SLA_MS;
    return {
      row: {
        framework: "gdpr",
        id: res.id,
        email: input.email,
        kind: normaliseKind(res.type),
        status: res.status as UnifiedStatus,
        submitted_at: nowMs,
        due_at: dueAt,
        sla_breached: false,
        region: input.region,
        raw: res,
      },
      verification_token: res.verification?.token,
    };
  }

  // CCPA
  const res = await apiClient.post<{
    id: string;
    type: string;
    status: string;
    received_at: number;
    verification_token?: string;
  }>(submitEndpointFor("ccpa"), {
    subject_email: input.email,
    ca_resident_attested: true,
    type: mapKindToCcpaType(input.kind),
  });
  const dueAt = res.received_at + CCPA_SLA_MS;
  return {
    row: {
      framework: "ccpa",
      id: res.id,
      email: input.email,
      kind: normaliseKind(res.type),
      status: res.status as UnifiedStatus,
      submitted_at: res.received_at,
      due_at: dueAt,
      sla_breached: false,
      region: "US-CA",
      raw: res,
    },
    verification_token: res.verification_token,
  };
}

/**
 * List open (non-terminal) requests for a framework. We fetch the raw list
 * and drop terminal rows client-side so the table always reflects "what
 * needs action". Terminal = fulfilled | rejected | denied.
 */
export async function listOpen(framework: DsrFramework): Promise<DsrRow[]> {
  try {
    if (framework === "gdpr") {
      const rows = await apiClient.get<GdprApiRow[]>(openListEndpointFor("gdpr"));
      return rows
        .map(normaliseGdpr)
        .filter((r) => r.status !== "fulfilled" && r.status !== "rejected");
    }
    const rows = await apiClient.get<CcpaApiRow[]>(openListEndpointFor("ccpa"));
    return rows
      .map(normaliseCcpa)
      .filter((r) => r.status !== "fulfilled" && r.status !== "denied");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

export interface UpdateStatusArgs {
  framework: DsrFramework;
  id: string;
  /** Target status. Must be valid for the framework. */
  status: UnifiedStatus;
  /** Optional denial/rejection reason (CCPA). */
  reason?: string;
}

export async function updateStatus(args: UpdateStatusArgs): Promise<DsrRow> {
  if (args.framework === "gdpr") {
    // GDPR uses PATCH; task copy says "POST" — we send both where possible.
    const res = await apiClient.patch<GdprApiRow>(
      statusEndpointFor("gdpr", args.id),
      { status: args.status },
    );
    return normaliseGdpr(res);
  }
  const body: { status: UnifiedStatus; denial_reason?: string } = {
    status: args.status,
  };
  if (args.reason) body.denial_reason = args.reason;
  const res = await apiClient.patch<CcpaApiRow>(
    statusEndpointFor("ccpa", args.id),
    body,
  );
  return normaliseCcpa(res);
}

// ---------------------------------------------------------------------------
// Status transitions available from the UI, per framework.
// ---------------------------------------------------------------------------

export const NEXT_STATUSES_FOR: Record<
  DsrFramework,
  Record<UnifiedStatus, UnifiedStatus[]>
> = {
  gdpr: {
    pending: ["in_progress", "rejected"],
    in_progress: ["fulfilled", "rejected"],
    fulfilled: [],
    rejected: [],
    received: [],
    verified: [],
    denied: [],
  },
  ccpa: {
    received: ["verified", "denied"],
    verified: ["fulfilled", "denied"],
    fulfilled: [],
    denied: [],
    pending: [],
    in_progress: [],
    rejected: [],
  },
};

export function nextStatusesFor(row: DsrRow): UnifiedStatus[] {
  return NEXT_STATUSES_FOR[row.framework][row.status] ?? [];
}

export const complianceDsrQueryKeys = {
  all: ["compliance-dsr"] as const,
  openFor: (f: DsrFramework) => [...complianceDsrQueryKeys.all, "open", f] as const,
};

export const _testInternals = {
  DAY_MS,
  EMAIL_RE,
};
